import { put, call, all, select } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import {
  apiMandatesGet,
  apiMandatesPost,
  apiMandatesHoldingsGet,
  apiMandatesHoldingsPost,
  apiMandatesHoldingsPatch,
  apiMandatesKpisGet,
  apiMandatePerformanceGet,
  apiMandatesPatch,
  apiMandatesDetailsGet,
  apiMandatesImportQuotesPost,
  apiMandatesMaxDrawdownGet,
  apiMandatesOptimizationGet,
} from '../api/mandate';
import {
  MANDATES_GET,
  MANDATES_POST,
  MANDATES_DETAILS_GET,
  MANDATES_HOLDINGS_GET,
  MANDATES_HOLDINGS_POST,
  MANDATES_HOLDINGS_PATCH,
  MANDATES_KPIS_GET,
  MANDATES_PERFORMANCE_GET,
  MANDATES_PATCH,
  MANDATES_IMPORT_QUOTES_POST,
  MANDATES_ALERT,
  MANDATES_MAX_DRAWDOWN_GET,
  MANDATES_OPTIMIZATION_GET,
} from '../actions/types';
import { createQadraSaga } from './shared';
import { adminPaths, withId } from '../../paths';
import { actionModalOpen, actionPushUrl } from '../actions';

function* mandatesGet({ payload } = {}) {
  const { mandates } = yield call(apiMandatesGet, payload);
  yield put({
    type: MANDATES_GET._SUCCESS,
    payload: { mandates, filters: payload },
  });
}

const watchMandatesGet = createQadraSaga(mandatesGet, {
  action: MANDATES_GET._REQUEST,
});

function* mandatesPost({ payload: { riskProfileId, name, mandateTypeName } }) {
  const { mandate } = yield call(apiMandatesPost, { riskProfileId, name, mandateTypeName });
  yield mandatesGet();
  yield put(push(withId(adminPaths.mandates.details, [mandate.id, 'mandateId'])));
}

const watchMandatesPost = createQadraSaga(mandatesPost, {
  action: MANDATES_POST._REQUEST,
});

function* mandateHoldingsGet({ payload: { mandateId } }) {
  const { holdings } = yield call(apiMandatesHoldingsGet, mandateId);
  yield put({
    type: MANDATES_HOLDINGS_GET._SUCCESS,
    payload: { mandateId, holdings },
  });
}

const watchMandateHoldingsGet = createQadraSaga(mandateHoldingsGet, {
  action: MANDATES_HOLDINGS_GET._REQUEST,
});

function* mandateDetailsGet({ payload: { mandateId } }) {
  const { mandate } = yield call(apiMandatesDetailsGet, mandateId);
  yield put({
    type: MANDATES_DETAILS_GET._SUCCESS,
    payload: { mandateId, mandate },
  });
}

const watchMandateDetailsGet = createQadraSaga(mandateDetailsGet, {
  action: MANDATES_DETAILS_GET._REQUEST,
});

function* mandatesPatch({ payload: { mandateId, published } }) {
  yield call(apiMandatesPatch, mandateId, { published });
  yield mandatesGet();
}

const watchMandatesPatch = createQadraSaga(mandatesPatch, {
  action: MANDATES_PATCH._REQUEST,
});

function* mandateKpisGet({ payload: { mandateId } }) {
  const { kpis } = yield call(apiMandatesKpisGet, mandateId);
  yield put({
    type: MANDATES_KPIS_GET._SUCCESS,
    payload: { mandateId, kpis },
  });
}

const watchMandateKpisGet = createQadraSaga(mandateKpisGet, {
  action: MANDATES_KPIS_GET._REQUEST,
});

function* mandateHoldingsPost({ payload: { mandateId, instrumentId, weight } }) {
  yield call(apiMandatesHoldingsPost, mandateId, { instrumentId, weight });
  yield mandateHoldingsGet({ payload: { mandateId } });
}

const watchMandateHoldingsPost = createQadraSaga(mandateHoldingsPost, {
  action: MANDATES_HOLDINGS_POST._REQUEST,
});

function* mandateImportQuotesPost({ payload: { mandateId, file } }) {
  const formData = new FormData();
  formData.append('file', new File([file.file || file], file.name || file));
  yield call(apiMandatesImportQuotesPost, mandateId, { file: formData });

  yield put({ type: MANDATES_ALERT, payload: { message: 'IMPORT_QUOTES_REFRESH', type: 'info' } });
}

const watchMandateImportQuotesPost = createQadraSaga(mandateImportQuotesPost, {
  action: MANDATES_IMPORT_QUOTES_POST._REQUEST,
});

function* mandatePerformanceGet({
  payload: { mandateId, benchmarkId, yearsTag, compareIds, startDate },
}) {
  const { chart } = yield call(apiMandatePerformanceGet, mandateId, {
    benchmarkId,
    yearsTag,
    compareIds,
    startDate,
  });

  yield put({
    type: MANDATES_PERFORMANCE_GET._SUCCESS,
    payload: {
      mandateId,
      performanceChartBenchmarkId: benchmarkId,
      performanceChart: chart,
      compareIds,
      performanceChartYearsTag: yearsTag,
    },
  });
}

const watchMandatePerformanceGet = createQadraSaga(mandatePerformanceGet, {
  action: MANDATES_PERFORMANCE_GET._REQUEST,
});

function* mandateHoldingsPatch({ payload: { mandateId, holdings } }) {
  const performance = yield select((s) => s.mandates.performance[mandateId]);

  yield call(apiMandatesHoldingsPatch, mandateId, { holdings });

  const updates = [
    mandateHoldingsGet({ payload: { mandateId } }),
    mandateKpisGet({ payload: { mandateId } }),
    mandateDetailsGet({ payload: { mandateId } }),
    // clear stale optimization
    put({
      type: MANDATES_OPTIMIZATION_GET._SUCCESS,
      payload: { optimization: undefined, mandateId },
    }),
  ];

  if (performance) {
    updates.push(
      mandatePerformanceGet({
        payload: {
          mandateId,
          benchmarkId: performance.performanceChartBenchmarkId,
          yearsTag: performance.performanceChartYearsTag,
        },
      })
    );
  }

  yield all(updates);
}

const watchMandateHoldingsPatch = createQadraSaga(mandateHoldingsPatch, {
  action: MANDATES_HOLDINGS_PATCH._REQUEST,
});

export function* mandatesMaxDrawdownGet({
  payload: { mandateId, startDate, endDate, benchmarkId, samplingInterval },
}) {
  const { maxDrawdown } = yield call(apiMandatesMaxDrawdownGet, {
    mandateId,
    startDate,
    endDate,
    benchmarkId,
    samplingInterval,
  });

  yield put({
    type: MANDATES_MAX_DRAWDOWN_GET._SUCCESS,
    payload: { maxDrawdown, mandateId },
  });
}

const watchMandatesMaxDrawdownGet = createQadraSaga(mandatesMaxDrawdownGet, {
  action: MANDATES_MAX_DRAWDOWN_GET._REQUEST,
});

export function* mandatesOptimizationGet({
  payload: { mandateId, targetReturn, forceRetry, url },
}) {
  const { optimization } = yield call(apiMandatesOptimizationGet, mandateId, {
    targetReturn,
    forceRetry,
  });

  yield put({
    type: MANDATES_OPTIMIZATION_GET._SUCCESS,
    payload: { optimization, targetReturn, mandateId },
  });

  // if in progress show a modal and wait for the notification
  if (optimization.status.toLowerCase() === 'inprogress') {
    yield put(actionModalOpen('ModalOptimizationRequestSuccessful'));
  }
  // else if error or success go to optimization page
  else if (url && ['success', 'error'].includes(optimization.status.toLowerCase())) {
    yield put(actionPushUrl(url));
  }
}

const watchMandatesOptimizationGet = createQadraSaga(mandatesOptimizationGet, {
  action: MANDATES_OPTIMIZATION_GET._REQUEST,
});

export default [
  watchMandatesGet,
  watchMandatesPost,
  watchMandatesPatch,
  watchMandateHoldingsGet,
  watchMandateHoldingsPost,
  watchMandateHoldingsPatch,
  watchMandateKpisGet,
  watchMandatePerformanceGet,
  watchMandateDetailsGet,
  watchMandateImportQuotesPost,
  watchMandatesMaxDrawdownGet,
  watchMandatesOptimizationGet,
];
